import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useSidebarTab } from '../contexts/SidebarTabContext';
import axios from 'axios';
import { API_BASE_URL } from '../config/apiConfig';
import { useMessage } from '../contexts/MessageContext'; // Importar el hook de mensajes
import MisPreguntas from './MisPreguntas'; // Importar el nuevo componente
import UserProfile from './UserProfile'; // Importar el componente de perfil
import UserModerationPanel from './UserModerationPanel'; // Importar el componente de moderación de usuarios
import FormularioComunidad from './FormularioComunidad'; // Importar el formulario de comunidad
import ModificarPreguntaModal from './ModificarPreguntaModal'; // Importar el componente de modificación de pregunta
import RevisionPreguntas from './RevisionPreguntas'; // Importar el componente de revisión de preguntas
import PropuestaAgenteModal from './PropuestaAgenteModal'; // Modal de gestión de propuestas del agente
import QuestionManagementSection from './QuestionManagementSection';
import DirectIntroSection from './direct-intro/DirectIntroSection';
import InicioConcurso from './InicioConcurso'; // Importar el componente de inicio de concurso
import PublicStatsSection from './PublicStatsSection';
import JuegoDiario from './juego-diario/JuegoDiario';
import { useUser } from '../contexts/UserContext'; // Importar el hook del contexto de usuario
import { useModerationContext } from '../contexts/ModerationContext'; // Importar el hook del contexto de moderación
import { motion, AnimatePresence } from 'framer-motion';

const LibraryHeaderIcon = ({ className = '' }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden="true">
    <path d="M4 5h5v15H4z" />
    <path d="M10 5h5v15h-5z" />
    <path d="M16 5h4v15h-4z" />
    <path d="M3 20h18" />
  </svg>
);

const AdminPanel = () => {
  const { user } = useUser(); // Obtener la información del usuario del contexto
  const { showMessage } = useMessage(); // Obtener la función de mensajes del contexto
  const { fetchPendingQuestionsCount } = useModerationContext(); // Obtener la función para actualizar el contador
  const { activeTab } = useSidebarTab();
  const panelViewportRef = useRef(null);
  const [preguntasPendientes, setPreguntasPendientes] = useState([]);
  const [preguntasTotales, setPreguntasTotales] = useState([]);
  const [loadingPreguntasTotales, setLoadingPreguntasTotales] = useState(false);
  const [preguntaSeleccionada, setPreguntaSeleccionada] = useState(null);
  const [mostrarModalModificar, setMostrarModalModificar] = useState(false);
  const [preguntasSeleccionadas, setPreguntasSeleccionadas] = useState([]);
  const [propuestasAgente, setPropuestasAgente] = useState([]);
  const [propuestaSeleccionada, setPropuestaSeleccionada] = useState(null);

 // Determinar el rol del usuario actual
  const userRole = user?.rol || 'usuario';

  useEffect(() => {
    // Solo cargar preguntas pendientes si el usuario puede verlas
    if (userRole === 'admin' || userRole === 'moderador') {
      fetchPreguntasPendientes();
      fetchPreguntasTotales();
      fetchPropuestasAgente();
    }
  }, [userRole]);

  useEffect(() => {
    const isMobile = typeof window !== 'undefined' && window.matchMedia('(max-width: 639px)').matches;
    if (!isMobile || !panelViewportRef.current) return;

    panelViewportRef.current.scrollTo({ top: 0, behavior: 'auto' });
  }, [activeTab]);

  const fetchPreguntasPendientes = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/preguntas_pendientes.php`);
      setPreguntasPendientes(response.data);
    } catch (error) {
      console.error('Error al cargar preguntas:', error);
    }
  };

  const fetchPropuestasAgente = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/revisiones_listar.php`, { params: { estado: 'en_proceso', limit: 300 } });
      if (response.data && response.data.success && Array.isArray(response.data.revisiones)) {
        setPropuestasAgente(response.data.revisiones);
      } else {
        setPropuestasAgente([]);
      }
    } catch (error) {
      console.error('Error al cargar propuestas del agente:', error);
      setPropuestasAgente([]);
    }
  };

  const handleAbrirPropuesta = (prop) => setPropuestaSeleccionada(prop);

  // Mapa id -> pregunta, para comparar duplicados en el modal del agente.
  const preguntasLookup = useMemo(() => {
    const m = {};
    preguntasTotales.forEach((p) => { m[String(p.id)] = p; });
    return m;
  }, [preguntasTotales]);

  const handlePropuestaResuelta = () => {
    fetchPropuestasAgente();
    fetchPreguntasTotales();
    fetchPendingQuestionsCount();
  };

  const fetchPreguntasTotales = async () => {
    try {
      setLoadingPreguntasTotales(true);
      const response = await axios.get(`${API_BASE_URL}/obtener_todas_preguntas.php`);
      if (response.data && response.data.success && Array.isArray(response.data.preguntas)) {
        setPreguntasTotales(response.data.preguntas);
      } else {
        setPreguntasTotales([]);
      }
    } catch (error) {
      console.error('Error al cargar todas las preguntas:', error);
      setPreguntasTotales([]);
    } finally {
      setLoadingPreguntasTotales(false);
    }
  };

  const aprobarPregunta = async (id) => {
    try {
      await axios.post(`${API_BASE_URL}/aprobar_pregunta.php`, { id });
      fetchPreguntasPendientes();
      fetchPreguntasTotales();
      fetchPendingQuestionsCount(); // Actualizar el contador en el contexto
    } catch (error) {
      console.error('Error al aprobar pregunta:', error);
    }
  };

  const rechazarPregunta = async (id) => {
    try {
      await axios.post(`${API_BASE_URL}/rechazar_pregunta.php`, { id });
      fetchPreguntasPendientes();
      fetchPreguntasTotales();
      fetchPendingQuestionsCount(); // Actualizar el contador en el contexto
    } catch (error) {
      console.error('Error al rechazar pregunta:', error);
    }
  };

  const aprobarTodasPreguntas = async () => {
    if (preguntasSeleccionadas.length === 0) return;
    
    const confirmacion = window.confirm(`¿Estás seguro de que quieres aprobar las ${preguntasSeleccionadas.length} preguntas seleccionadas?`);
    if (!confirmacion) return;
    
    try {
      // Usar Promise.allSettled para manejar posibles fallos individuales
      const promises = preguntasSeleccionadas.map(id => 
        axios.post(`${API_BASE_URL}/aprobar_pregunta.php`, { id })
      );
      const resultados = await Promise.allSettled(promises);
      
      // Contar cuántas operaciones tuvieron éxito y cuántas fallaron
      const exitos = resultados.filter(resultado => resultado.status === 'fulfilled').length;
      const fallos = resultados.filter(resultado => resultado.status === 'rejected').length;
      
      fetchPreguntasPendientes(); // Actualizar la lista de preguntas pendientes
      fetchPreguntasTotales();
      fetchPendingQuestionsCount(); // Actualizar el contador en el contexto
      setPreguntasSeleccionadas([]); // Limpiar selección después de la operación
      
      if (fallos === 0) {
        showMessage(`¡Todas las preguntas (${exitos}) han sido aprobadas!`, 'success');
      } else {
        showMessage(`Operación completada parcialmente. Aprobadas: ${exitos}, Fallidas: ${fallos}. Se han recargado las preguntas pendientes.`, 'warning');
      }
    } catch (error) {
      console.error('Error al aprobar todas las preguntas:', error);
      showMessage('Hubo un error general al aprobar las preguntas.', 'error');
    }
  };

 const rechazarTodasPreguntas = async () => {
    if (preguntasSeleccionadas.length === 0) return;
    
    const confirmacion = window.confirm(`¿Estás seguro de que quieres rechazar las ${preguntasSeleccionadas.length} preguntas seleccionadas?`);
    if (!confirmacion) return;
    
    try {
      // Usar Promise.allSettled para manejar posibles fallos individuales
      const promises = preguntasSeleccionadas.map(id => 
        axios.post(`${API_BASE_URL}/rechazar_pregunta.php`, { id })
      );
      const resultados = await Promise.allSettled(promises);
      
      // Contar cuántas operaciones tuvieron éxito y cuántas fallaron
      const exitos = resultados.filter(resultado => resultado.status === 'fulfilled').length;
      const fallos = resultados.filter(resultado => resultado.status === 'rejected').length;
      
      fetchPreguntasPendientes(); // Actualizar la lista de preguntas pendientes
      fetchPreguntasTotales();
      fetchPendingQuestionsCount(); // Actualizar el contador en el contexto
      setPreguntasSeleccionadas([]); // Limpiar selección después de la operación
      
      if (fallos === 0) {
        showMessage(`¡Todas las preguntas (${exitos}) han sido rechazadas!`, 'success');
      } else {
        showMessage(`Operación completada parcialmente. Rechazadas: ${exitos}, Fallidas: ${fallos}. Se han recargado las preguntas pendientes.`, 'warning');
      }
    } catch (error) {
      console.error('Error al rechazar todas las preguntas:', error);
      showMessage('Hubo un error general al rechazar las preguntas.', 'error');
    }
  };

  const modificarPregunta = async (datosActualizados) => {
    try {
      // Validar que todos los campos requeridos estén presentes
      if (!datosActualizados.id || !datosActualizados.pregunta || !datosActualizados.respuesta_correcta || 
          !datosActualizados.categoria || !datosActualizados.etiqueta || !datosActualizados.dificultad) {
        console.error('Faltan datos requeridos para modificar la pregunta.');
        return;
      }

      // Asegurar que los campos requeridos no estén vacíos
      const datosParaEnviar = {
        id: datosActualizados.id,
        pregunta: datosActualizados.pregunta.trim(),
        respuesta_correcta: datosActualizados.respuesta_correcta.trim(),
        respuesta_falsa1: datosActualizados.respuesta_falsa1 ? datosActualizados.respuesta_falsa1.trim() : '',
        respuesta_falsa2: datosActualizados.respuesta_falsa2 ? datosActualizados.respuesta_falsa2.trim() : '',
        respuesta_falsa3: datosActualizados.respuesta_falsa3 ? datosActualizados.respuesta_falsa3.trim() : '',
        categoria: datosActualizados.categoria,
        etiqueta: datosActualizados.etiqueta,
        dificultad: datosActualizados.dificultad,
        explicacion: datosActualizados.explicacion ? datosActualizados.explicacion.trim() : '',
        imagen: typeof datosActualizados.imagen === 'string' ? datosActualizados.imagen.trim() : '',
        imagen_temp_token: typeof datosActualizados.imagen_temp_token === 'string' ? datosActualizados.imagen_temp_token.trim() : ''
      };

      // Validar nuevamente después de aplicar trim
      if (!datosParaEnviar.pregunta || !datosParaEnviar.respuesta_correcta || !datosParaEnviar.categoria || !datosParaEnviar.etiqueta || !datosParaEnviar.dificultad) {
        console.error('Los campos obligatorios no pueden estar vacíos.');
        return;
      }

      const response = await axios.post(`${API_BASE_URL}/actualizar_pregunta.php`, datosParaEnviar);

      // Verificar si la respuesta es exitosa
      if (response.status === 200) {
        if (response.data && response.data.message) {
          // Actualizar la lista de preguntas pendientes
          fetchPreguntasPendientes();
          fetchPreguntasTotales();
          // Cerrar el modal
          setMostrarModalModificar(false);
          setPreguntaSeleccionada(null);
          // Mostrar mensaje de éxito
          showMessage('¡Pregunta modificada exitosamente!', 'success');
        } else if (response.data && response.data.error) {
          console.error('Error en la respuesta del servidor:', response.data.error);
          showMessage(`Error al modificar la pregunta: ${response.data.error}`, 'error');
        } else {
          console.error('Respuesta inesperada del servidor:', response);
          showMessage('Error desconocido al modificar la pregunta. La respuesta del servidor no tiene el formato esperado.', 'error');
        }
      } else {
        console.error('Error en la respuesta del servidor:', response);
        showMessage(`Error en la respuesta del servidor: ${response.status}`, 'error');
      }
    } catch (error) {
      console.error('Error al modificar pregunta:', error);
      if (error.response) {
        // El servidor respondió con un código de error
        console.error('Error en la respuesta del servidor:', error.response.data.error || `Código de error: ${error.response.status}`);
        showMessage(`Error al modificar la pregunta: ${error.response.data.error || `Código de error: ${error.response.status}`}`, 'error');
      } else if (error.request) {
        // La solicitud fue hecha pero no hubo respuesta
        console.error('Error de red: No se recibió respuesta del servidor.');
        showMessage('Error de red: No se recibió respuesta del servidor.', 'error');
      } else {
        // Algo ocurrió al preparar la solicitud
        console.error('Error al preparar la solicitud:', error.message);
        showMessage(`Error al preparar la solicitud: ${error.message}`, 'error');
      }
    }
  };

  const handleModificarPregunta = async (pregunta) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/obtener_todas_preguntas.php`);
      const preguntasActualizadas = response.data?.preguntas || [];
      const preguntaActual = preguntasActualizadas.find((p) => Number(p.id) === Number(pregunta.id));
      
      if (!preguntaActual) {
        showMessage('La pregunta ya no existe en la base de datos.', 'error');
        fetchPreguntasPendientes();
        fetchPreguntasTotales();
        return;
      }
      
      setPreguntaSeleccionada(preguntaActual);
      setMostrarModalModificar(true);
    } catch (error) {
      console.error('Error al obtener los datos actuales de la pregunta:', error);
      // Si hay error, usar los datos disponibles y permitir la edición de todas formas
      setPreguntaSeleccionada(pregunta);
      setMostrarModalModificar(true);
    }
  };

  const eliminarPregunta = async (id) => {
    const confirmacion = window.confirm('¿Seguro que quieres borrar esta pregunta de forma definitiva?');
    if (!confirmacion) return;

    try {
      const response = await axios.post(`${API_BASE_URL}/eliminar_pregunta.php`, { id });
      if (response.data?.success) {
        showMessage('Pregunta eliminada correctamente.', 'success');
        if (preguntaSeleccionada && Number(preguntaSeleccionada.id) === Number(id)) {
          setMostrarModalModificar(false);
          setPreguntaSeleccionada(null);
        }
      } else {
        showMessage(response.data?.message || 'No se pudo eliminar la pregunta.', 'error');
      }
      fetchPreguntasPendientes();
      fetchPreguntasTotales();
    } catch (error) {
      console.error('Error al eliminar pregunta:', error);
      const message = error.response?.data?.message || 'Error de red al eliminar la pregunta.';
      showMessage(message, 'error');
    }
  };

  const panelContentClass = 'w-full max-w-full bg-white rounded-lg shadow-md dark:bg-gray-800 dark:text-white transition-colors duration-300 p-3 sm:p-4 md:p-5 min-h-0 flex flex-col overflow-x-hidden';

  return (
    <section ref={panelViewportRef} data-admin-panel-scroll="true" className="relative flex flex-col h-[calc(100dvh-4rem)] overflow-hidden px-2 pt-12 pb-4 sm:px-4 lg:px-6 lg:pt-4">
      <div className="relative z-10 flex-1 min-h-0 flex flex-col w-full max-w-full overflow-x-hidden">
        <AnimatePresence mode="wait">
        {activeTab === 'perfil' && (
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 20 }}
            transition={{ duration: 0.3 }}
            key="perfil"
            className="mx-auto w-full max-w-7xl flex-1 min-h-0 flex flex-col overflow-y-auto overflow-x-hidden overscroll-contain"
          >
            <div className={panelContentClass}>
              <UserProfile />
            </div>
          </motion.div>
        )}
        {(userRole === 'admin' || userRole === 'moderador') && activeTab === 'revision-preguntas' && (
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 20 }}
            transition={{ duration: 0.3 }}
            key="revision-preguntas"
            className="mx-auto w-full max-w-7xl flex-1 min-h-0 flex flex-col overflow-y-auto overflow-x-hidden overscroll-contain"
          >
            <div className={panelContentClass}>
              <RevisionPreguntas
                preguntas={preguntasPendientes}
                preguntasSeleccionadas={preguntasSeleccionadas}
                setPreguntasSeleccionadas={setPreguntasSeleccionadas}
                aprobarPregunta={aprobarPregunta}
                rechazarPregunta={rechazarPregunta}
                handleModificarPregunta={handleModificarPregunta}
                aprobarTodasPreguntas={aprobarTodasPreguntas}
                rechazarTodasPreguntas={rechazarTodasPreguntas}
                propuestasAgente={propuestasAgente}
                onAbrirPropuesta={handleAbrirPropuesta}
              />
            </div>
          </motion.div>
        )}
        {(userRole === 'admin' || userRole === 'moderador') && activeTab === 'gestion-preguntas' && (
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 20 }}
            transition={{ duration: 0.3 }}
            key="gestion-preguntas"
            className="mx-auto w-full max-w-7xl flex-1 min-h-0 flex flex-col overflow-y-auto overflow-x-hidden overscroll-contain"
          >
            <div className={`${panelContentClass} flex-1 min-h-0`}>
              <QuestionManagementSection
                preguntas={preguntasTotales}
                isLoading={loadingPreguntasTotales}
                onRefresh={fetchPreguntasTotales}
                onEdit={handleModificarPregunta}
              />
            </div>
          </motion.div>
        )}
        {activeTab === 'introduccion-directa' && (
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 20 }}
            transition={{ duration: 0.3 }}
            key="introduccion-directa"
            className="mx-auto w-full max-w-7xl flex-1 min-h-0 flex flex-col overflow-y-auto overflow-x-hidden overscroll-contain"
          >
            <div className={panelContentClass}>
              <DirectIntroSection />
            </div>
          </motion.div>
        )}
        {activeTab === 'inicio-concurso' && (
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 20 }}
            transition={{ duration: 0.3 }}
            key="inicio-concurso"
            className="mx-auto w-full max-w-7xl flex-1 min-h-0 flex flex-col overflow-y-auto overflow-x-hidden overscroll-contain"
          >
            <div className={panelContentClass}>
              <InicioConcurso />
            </div>
          </motion.div>
        )}
        {activeTab === 'mis-preguntas' && (
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 20 }}
            transition={{ duration: 0.3 }}
            key="mis-preguntas"
            className="mx-auto w-full max-w-7xl flex-1 min-h-0 flex flex-col overflow-y-auto overflow-x-hidden overscroll-contain"
          >
            <div className={panelContentClass}>
              <MisPreguntas />
            </div>
          </motion.div>
        )}
        {activeTab === 'estadisticas-publicas' && (
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 20 }}
            transition={{ duration: 0.3 }}
            key="estadisticas-publicas"
            className="mx-auto w-full max-w-7xl flex-1 min-h-0 flex flex-col overflow-y-auto overflow-x-hidden overscroll-contain"
          >
            <div className={panelContentClass}>
              <PublicStatsSection />
            </div>
          </motion.div>
        )}
        {(userRole === 'admin' || userRole === 'moderador') && activeTab === 'moderacion-usuarios' && (
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 20 }}
            transition={{ duration: 0.3 }}
            key="moderacion-usuarios"
            className="mx-auto w-full max-w-7xl flex-1 min-h-0 flex flex-col overflow-y-auto overflow-x-hidden overscroll-contain"
          >
            <div className={panelContentClass}>
              <UserModerationPanel />
            </div>
          </motion.div>
        )}
        {userRole === 'usuario' && activeTab === 'juego-diario' && (
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 20 }}
            transition={{ duration: 0.3 }}
            key="juego-diario"
            className="mx-auto w-full max-w-7xl flex-1 min-h-0 flex flex-col overflow-y-auto overflow-x-hidden overscroll-contain"
          >
            <div className={panelContentClass}>
              <JuegoDiario />
            </div>
          </motion.div>
        )}
        {userRole === 'usuario' && activeTab === 'formulario' && (
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 20 }}
            transition={{ duration: 0.3 }}
            key="formulario"
            className="mx-auto w-full max-w-7xl flex-1 min-h-0 flex flex-col overflow-y-auto overflow-x-hidden overscroll-contain"
          >
            <div className={panelContentClass}>
              <header className="mb-4 flex items-center gap-3 text-gray-800 dark:text-gray-100">
                <LibraryHeaderIcon className="w-6 h-6 text-gray-900 dark:text-white" />
                <div>
                  <h3 className="text-base md:text-lg font-semibold">Formulario de comunidad</h3>
                  <p className="text-xs md:text-sm text-gray-500 dark:text-gray-300">Comparte tu pregunta para revisión del equipo.</p>
                </div>
              </header>
              <FormularioComunidad />
            </div>
          </motion.div>
        )}
        </AnimatePresence>
      </div>
      {/* Modal para modificar pregunta */}
      <AnimatePresence>
        {mostrarModalModificar && preguntaSeleccionada && (
          <ModificarPreguntaModal
            key={`modificar-${preguntaSeleccionada.id}`}
            pregunta={preguntaSeleccionada}
            isOpen={mostrarModalModificar}
            onClose={() => {
              setMostrarModalModificar(false);
              setPreguntaSeleccionada(null);
            }}
            onSave={modificarPregunta}
            onDelete={eliminarPregunta}
          />
        )}
      </AnimatePresence>

      {/* Modal de gestión de propuestas del agente */}
      <AnimatePresence>
        {propuestaSeleccionada && (
          <PropuestaAgenteModal
            key={`propuesta-${propuestaSeleccionada.id}`}
            propuesta={propuestaSeleccionada}
            isOpen={Boolean(propuestaSeleccionada)}
            preguntasLookup={preguntasLookup}
            onClose={() => setPropuestaSeleccionada(null)}
            onResolved={handlePropuestaResuelta}
          />
        )}
      </AnimatePresence>
    </section>
  );
};

export default AdminPanel;
